import React from 'react'
import SourceSearch, {
  SourceSearchPageStructureTop,
} from '@chegg-wt/core/components/Layouts/SourceSearch'
import { getLvpLayout } from '@chegg-wt/core/components/Layouts/LargeVideoPlayerLayout'
import { NextPageWithLayout } from '@chegg-wt/core/types/layout'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import { getSourceTypeName, sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { useRouter } from 'next/router'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import useUserRole from '@chegg-wt/core/hooks/useUserRole'
import useRedirect from '@chegg-wt/core/hooks/useRedirect'

import { NextPageContext } from 'next'
import {
  getCapitalizedSEONameOfStyle,
  isStyleIdInValid,
} from '@chegg-wt/core/utils/styles'
import { getUrlStyleNameV2 } from '@chegg-wt/core/api/oneGraph'
import { UserRoles } from '@chegg-wt/core/constants/user'
import MetaDescription from '@chegg-wt/core/components/Head/MetaDescription'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { useShowDiscountModal } from '@chegg-wt/core/hooks/useShowDiscountModal'
import upgradeCopy from '../../../copy/upgrade'
import considerCredibility from '../../../copy/credibility'
import { getSourceFromQuery } from '../../../utils/routes'

interface SourceSearchPageProps {
  initialUrlStyleName: string
}

const SourceSearchPage: NextPageWithLayout<SourceSearchPageProps> = ({
  initialUrlStyleName,
}) => {
  useShowDiscountModal()
  const { style, source } = useRouter().query as { style: string; source: string }
  const userRole = useUserRole()
  const { toDashboard } = useRoutesContext()
  useRedirect({
    shouldRedirect: userRole === UserRoles.PRO_USER,
    redirectRoute: toDashboard(),
  })
  const formattedStyle = getCapitalizedSEONameOfStyle(style)
  useOnUnloadTimeOnPage()

  return (
    <>
      <SEOTitle
        title={`${formattedStyle} ${getSourceTypeName(
          source
        )} Citation Generator | EasyBib`}
      />
      <MetaDescription
        content={`Cite a ${source} in ${
          style === 'mla' ? style.toUpperCase() : style
        } with EasyBib. Automatically create your works cited page and format your bibliography.`}
      />
      <SourceSearch
        copy={{ upgradeCopy, considerCredibility }}
        styleId={style}
        initialUrlStyleName={initialUrlStyleName}
        initialSourceType={source}
      />
    </>
  )
}

export const getServerSideProps = async ({ query }: NextPageContext) => {
  const { style, source: sourceQuery } = query as { style: string; source: string }
  const [initialUrlStyleName] = await getUrlStyleNameV2(style)
  const source = getSourceFromQuery(
    { route: '/[style]/[source]/search-form' },
    sourceQuery
  )

  return {
    props: {
      initialUrlStyleName,
      isDefaultStyle: false,
      currentSourceType: source,
    },
    notFound: isStyleIdInValid(style) || !sourceTypeById(source),
  }
}

SourceSearchPage.getPageLayout = (page, ssrProps) =>
  getLvpLayout(page, () => <SourceSearchPageStructureTop {...ssrProps} />)

export default SourceSearchPage
